import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  LinearProgress,
  useTheme,
  alpha,
  Avatar,
  Grid,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserStartups } from '../store/slices/adminSlice';
import { getFileLink } from '../utils/getFileLink';

const UserStartupsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId } = useParams();
  const { userStartups, loading } = useSelector((state) => state.admin);

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserStartups(userId));
    }
  }, [dispatch, userId]);

  const truncateDescription = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  if (loading) return <LinearProgress />;

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 4, 
            color: theme.palette.primary.main,
            fontWeight: 'bold' 
          }}
        >
          Startuplar
        </Typography>

        <Stack spacing={3}>
          {userStartups?.map((startup) => (
            <Card
              key={startup.startupId}
              sx={{
                borderRadius: 2,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={1}>
                    <Avatar
                      src={getFileLink(startup.startupProfile?.mainLogoPath)}
                      alt={startup.startupProfile?.name}
                      sx={{ width: 64, height: 64 }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={7}>
                    <Typography variant="h6" gutterBottom>
                      {startup.startupProfile?.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {truncateDescription(startup.startupProfile?.shortDescription)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Toplanan / Hedef
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="medium">
                          {Number(startup.startupBalance?.qualifiedAmount || 0).toLocaleString('tr-TR')} ₺ / {Number(startup.startupBalance?.totalLimit || 0).toLocaleString('tr-TR')} ₺
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Açık Limit
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="medium" color="success.main">
                          {Number(startup.startupBalance?.availableLimit || 0).toLocaleString('tr-TR')} ₺
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={1}>
                    <Button
                      variant="contained"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/startuplar/detay/${startup.startupId}`)}
                      sx={{
                        whiteSpace: 'nowrap',
                        bgcolor: theme.palette.primary.main,
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark,
                        },
                      }}
                    >
                      Detay
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {userStartups?.length === 0 && (
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Kullanıcıya ait startup bulunamadı
              </Typography>
            </Card>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

export default UserStartupsPage; 